import {
  useApiUrl,
  useDataProvider,
  useInvalidate,
  useNotification,
} from "@refinedev/core";
import moment from "moment";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import { parseRead } from "@/lib/zod/read-schema";
import {
  EventUpdatesAttributes,
  EventUpdatesType,
} from "@/pages/events/features/notifications/event-updates-context";
import { EventAIEnrichmentType } from "@/pages/events/schemas/event.aienrichment.form";
import {
  EventBasicInfoType,
  EventDraftType,
  draftEventSchema,
} from "@/pages/events/schemas/event.create.form";
import {
  EventEditFormType,
  eventEditSchema,
  withCuratorConfidence,
} from "@/pages/events/schemas/event.edit.form";
import { readReach, withReach } from "@/pages/events/schemas/event.reach";

import {
  EventNestedType,
  eventNestedSchema,
} from "../schemas/event.nested.form";

interface GenreDbDto {
  id: string;
  name: string;
}
interface LineupSummary {
  id?: string;
  name: string;
}
interface MediaDbDto {
  photos?: Array<{ url: string }>;
}
interface EventVenueSummary {
  id: string;
  name: string;
  address: string;
  area?: string | null;
  geodata?: Record<string, unknown> | null;
  type?: string | null;
  media: MediaDbDto;
}

export interface EventSummary {
  id: string;
  name: string;
  slug?: string | null;
  startDate?: number | null;
  endDate?: number | null;
  type: string;
  genres?: GenreDbDto[];
  lineup?: LineupSummary[];
  media?: MediaDbDto | null;
  venue?: EventVenueSummary | null;
  venueTba?: boolean;
}

export const useEventRepository = () => {
  const apiUrl = useApiUrl();
  const { custom, getOne } = useDataProvider()();
  const { open } = useNotification();
  const invalidate = useInvalidate();

  async function search(query: string): Promise<EventNestedType[]> {
    try {
      if (!query) return [];
      const response = await custom?.({
        url: `${apiUrl}/events/explore`,
        method: "get",
        query: {
          q: query ?? "*",
          queryBy: "venue.name,name",
          limit: 25,
          page: 0,
          filters: query.startsWith('"') ? `name:=${query}` : "",
          sortBy: "_text_match:desc",
          disableCaching: true,
        },
      });
      if (!response?.data) return [];
      return response?.data?.hits?.map((i: any) =>
        eventNestedSchema.parse({
          ...i?.document,
          photo: i?.document?.media?.photos?.[0],
        }),
      );
    } catch (ex) {
      console.error("Event search failed due to:", ex);
      open?.({
        message: `Event search failed due to ${toHumanErrorMessage(ex)}`,
        type: "error",
      });
      return [];
    }
  }

  async function get(id: string): Promise<EventEditFormType> {
    const response = await getOne({ id, resource: "events" });
    // Lenient read + raw fallback: a hard `.parse()` here blanked the
    // whole event edit page on one unexpected field.
    const event = parseRead(eventEditSchema, response, "event");
    return { ...event, reach: readReach(event.metadata) };
  }

  async function getDraft(id: string): Promise<EventDraftType> {
    const response = (await getOne({ id, resource: "events" })) as any;
    if (response.tickets && response.tickets.length > 0) {
      response.ticketUrl = response.tickets[0].url;
    }
    response.reach = readReach(response.metadata);
    return draftEventSchema.parse(response);
  }

  async function clearTag(tagToRemove: string) {
    try {
      await custom?.({
        url: `${apiUrl}/events/clear-tag`,
        method: "post",
        payload: { tag: tagToRemove },
      });
      open?.({
        message: "Successfully cleaned tags",
        type: "success",
      });
      setTimeout(
        () =>
          invalidate({
            resource: "events",
            invalidates: ["list"],
          }),
        4000,
      );
    } catch (ex) {
      console.error("Failed to patch event due to:", ex);
      open?.({
        message: `Failed to patch event due to ${ex}`,
        type: "error",
      });
    }
  }

  async function patch(id: string, tags: string[], tagsToRemove: string[]) {
    Sentry.addBreadcrumb({
      category: "events.patch",
      message: "Patching event tags",
      level: "info",
      data: { id },
    });
    try {
      await custom?.({
        url: `${apiUrl}/events`,
        method: "patch",
        payload: { id, tags, tagsToRemove },
      });
      open?.({
        message: "Successfully patched event",
        type: "success",
      });
      setTimeout(
        () =>
          invalidate({
            resource: "events",
            invalidates: ["list"],
          }),
        2000,
      );
    } catch (ex) {
      Sentry.addBreadcrumb({
        category: "events.patch",
        message: "Failed to patch event tags",
        level: "error",
        data: { id },
      });
      Sentry.captureException(ex);
      console.error("Failed to patch event due to:", ex);
      open?.({
        message: `Failed to patch event due to ${ex}`,
        type: "error",
      });
    }
  }

  async function finaliseGiveaway(
    eventId: string,
    tickets: any,
    notify: boolean,
  ) {
    Sentry.addBreadcrumb({
      category: "events.giveaway",
      message: "Finalising giveaway",
      level: "info",
      data: { eventId, notify },
    });
    try {
      const response = await custom?.({
        url: `${apiUrl}/events/${eventId}/giveaway`,
        method: "post",
        payload: {
          id: eventId,
          ticketClaims: tickets,
          notify: notify,
        },
      });

      open?.({
        type: "success",
        message: "Successfully submitted giveaway",
        description: (response?.data?.errors ?? []).join("\n"),
      });
    } catch (e: any) {
      Sentry.addBreadcrumb({
        category: "events.giveaway",
        message: "Failed to finalise giveaway",
        level: "error",
        data: { eventId },
      });
      Sentry.captureException(e);
      console.error(e);
      open?.({
        type: "error",
        message: "Failed to submit giveaway",
        description: e?.response?.data?.detail,
      });
    }
  }

  function triggerBusinessEvent(
    id: string,
    type: EventUpdatesType,
    options?: EventUpdatesAttributes,
  ) {
    Sentry.addBreadcrumb({
      category: "events.business-event",
      message: `Triggering ${type}`,
      level: "info",
      data: { id, type },
    });
    custom?.({
      url: `${apiUrl}/events/trigger-business-event`,
      method: "post",
      payload: {
        eventId: id,
        eventType: type.toUpperCase(),
        options: {
          event_id: id,
          venue_id: options?.venueId,
          artist_ids: options?.artistIds,
          regions: options?.regions,
          similar_event_ids: options?.similarEventIds,
          genre_ids: options?.genreIds,
          exclude_genre_ids: options?.excludeGenreIds,
        },
      },
    })
      .then((data) => {
        console.log("Trigger business event ", data);
        open?.({
          message: `Triggered ${type} for event ${id}`,
          type: "success",
        });
      })
      .catch((e) => {
        Sentry.addBreadcrumb({
          category: "events.business-event",
          message: `Failed to trigger ${type}`,
          level: "error",
          data: { id },
        });
        Sentry.captureException(e);
        console.error("Trigger business event failed", e);
        open?.({
          message: `Failed due to ${e.response.data}`,
          type: "error",
        });
      });
  }

  async function draft(
    data: EventBasicInfoType & { id?: string },
  ): Promise<string> {
    Sentry.addBreadcrumb({
      category: "events.draft",
      message: data.id ? "Updating event draft" : "Creating event draft",
      level: "info",
      data: { id: data.id, name: data.name },
    });
    try {
      const response = await custom?.({
        url: `${apiUrl}/events/draft`,
        method: "post",
        payload: withReach(data),
      });
      const event = response?.data;
      Sentry.addBreadcrumb({
        category: "events.draft",
        message: "Draft saved",
        level: "info",
        data: { id: event?.id },
      });
      open?.({
        message: `Successfully ${data.id ? "updated" : "created"} draft`,
        type: "success",
      });
      return event!.id!.toString();
    } catch (e: any) {
      Sentry.addBreadcrumb({
        category: "events.draft",
        message: "Failed to save draft",
        level: "error",
        data: { id: data.id },
      });
      Sentry.captureException(e);
      console.error(e);
      open?.({
        message: `Failed to ${data.id ? "update" : "create"} draft`,
        description: e?.response?.data?.detail,
        type: "error",
      });
      throw e;
    }
  }

  async function complete(data: EventDraftType): Promise<string> {
    Sentry.addBreadcrumb({
      category: "events.complete",
      message: "Completing event creation",
      level: "info",
      data: { id: data.id, name: data.name },
    });
    try {
      const response = await custom?.({
        url: `${apiUrl}/events`,
        method: "post",
        payload: withReach(data),
      });
      const event = response?.data;
      Sentry.addBreadcrumb({
        category: "events.complete",
        message: "Event created successfully",
        level: "info",
        data: { id: event?.id },
      });
      open?.({
        message: `Successfully created event`,
        type: "success",
      });
      return event!.id!.toString();
    } catch (e: any) {
      Sentry.addBreadcrumb({
        category: "events.complete",
        message: "Failed to complete event creation",
        level: "error",
        data: { id: data.id },
      });
      Sentry.captureException(e);
      console.error(e);
      open?.({
        message: `Failed to create event`,
        description: e?.response?.data?.detail,
        type: "error",
      });
      throw e;
    }
  }

  async function update(
    data: EventEditFormType,
    opts?: { clearPlaceholderArt?: boolean },
  ): Promise<string> {
    Sentry.addBreadcrumb({
      category: "events.update",
      message: "Updating event",
      level: "info",
      data: { id: data.id, name: data.name },
    });
    try {
      const response = await custom?.({
        url: `${apiUrl}/events`,
        method: "put",
        payload: withReach({
          ...data,
          // Membership changes only through the group routes. The backend keeps
          // the stored group whatever this body says, so the form's read-only
          // copy of it is left out rather than sent stale.
          group: undefined,
          // A curator edit is a full-confidence signal: re-stamp an existing
          // pipeline confidence to 1 (the backend full-replaces metadata).
          metadata: withCuratorConfidence(data.metadata),
          slug: data.slug ?? "",
          ...(opts?.clearPlaceholderArt ? { hasPlaceholderArt: false } : {}),
        }),
      });
      const event = response?.data;
      Sentry.addBreadcrumb({
        category: "events.update",
        message: "Event updated successfully",
        level: "info",
        data: { id: event?.id },
      });
      open?.({
        message: `Successfully updated event`,
        type: "success",
      });
      return event!.id!.toString();
    } catch (e: any) {
      Sentry.addBreadcrumb({
        category: "events.update",
        message: "Failed to update event",
        level: "error",
        data: { id: data.id },
      });
      Sentry.captureException(e);
      console.error(e);
      open?.({
        message: `Failed to update event`,
        description: e?.response?.data?.detail,
        type: "error",
      });
      throw e;
    }
  }

  async function exportOrders(values: EventEditFormType) {
    try {
      const response = await custom?.({
        url: `${apiUrl}/events/${values.id}/export-orders`,
        method: "post",
        meta: { responseType: "blob" },
      });
      if (response?.data && response.data instanceof Blob) {
        const csvURL = window.URL.createObjectURL(response.data);
        const tempLink = document.createElement("a");
        tempLink.href = csvURL;
        tempLink.setAttribute(
          "download",
          `${moment(values.startDate).format("YYYYMMDD")}_${
            values.venue?.name ? values.venue.name + "_" : ""
          }${values.name}`,
        );
        tempLink.click();
      }
    } catch (e: any) {
      console.error(e);
      open?.({
        message: `Failed to export orders`,
        description: e?.response?.data?.detail,
        type: "error",
      });
    }
  }

  async function triggerEnrich(id: string): Promise<boolean> {
    Sentry.addBreadcrumb({
      category: "events.enrich",
      message: "Triggering event enrichment",
      level: "info",
      data: { id },
    });
    try {
      await custom?.({
        url: `${apiUrl}/events/enrich`,
        method: "post",
        payload: { id },
      });
      open?.({
        type: "success",
        message: "Enrichment triggered",
        description: "Event enrichment has been queued for processing.",
      });
      return true;
    } catch (error) {
      Sentry.addBreadcrumb({
        category: "events.enrich",
        message: "Failed to trigger event enrichment",
        level: "error",
        data: { id },
      });
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to trigger enrichment",
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  }

  async function updateEnrichment(
    id: string,
    enrichment: EventAIEnrichmentType,
  ): Promise<boolean> {
    try {
      await custom?.({
        url: `${apiUrl}/events/enrich`,
        method: "put",
        payload: { id, enrichment },
      });
      open?.({
        type: "success",
        message: "Enrichment approved",
        description: "The enrichment data has been saved successfully.",
      });
      return true;
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to save enrichment",
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  }

  async function nearestNeighbours(
    embedding: number[],
    limit: number,
  ): Promise<EventSummary[]> {
    const response = await custom?.({
      url: `${apiUrl}/search/nearest-neighbours-by-embedding`,
      method: "post",
      payload: { Embedding: embedding, TargetType: "event", Limit: limit },
    });
    return (response?.data as EventSummary[]) ?? [];
  }

  return {
    get,
    search,
    getDraft,
    clearTag,
    patch,
    finaliseGiveaway,
    triggerBusinessEvent,
    draft,
    complete,
    update,
    exportOrders,
    triggerEnrich,
    updateEnrichment,
    nearestNeighbours,
  };
};
